
/**
 * Horizon focus management
 * ZF accessibility update
 *
 * - Excludes hidden, inert, and collapsed content
 * - Recalculates focusable items as content changes
 * - Supports menu drawers, cart drawers, and dialogs
 */

const trapFocusHandlers = {};

const FOCUSABLE_SELECTOR = [
  'summary',
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  '[contenteditable="true"]',
  '[tabindex]'
].join(', ');

/**
 * Check whether an element is genuinely
 * available to keyboard users.
 */
function isFocusable(element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element.tabIndex < 0) {
    return false;
  }

  if (element.matches(':disabled')) {
    return false;
  }

  if (
    element.closest(
      '[hidden], [inert], [aria-hidden="true"]'
    )
  ) {
    return false;
  }

  // Do not include descendants of closed
  // native details elements. Their direct
  // summary remains available.
  const closedDetails = element.closest(
    'details:not([open])'
  );

  if (closedDetails) {
    const summary = closedDetails.querySelector(
      ':scope > summary'
    );

    if (summary !== element) {
      return false;
    }
  }

  if (element.getClientRects().length === 0) {
    return false;
  }

  const style = getComputedStyle(element);

  if (
    style.display === 'none' ||
    style.visibility !== 'visible'
  ) {
    return false;
  }

  return true;
}

/**
 * Return only currently accessible
 * and keyboard-focusable elements.
 */
function getFocusableElements(container) {
  if (!(container instanceof HTMLElement)) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      FOCUSABLE_SELECTOR
    )
  ).filter(isFocusable);
}

/**
 * Trap focus inside an active drawer,
 * menu, or dialog.
 */
export function trapFocus(container) {
  removeTrapFocus();

  if (!(container instanceof HTMLElement)) {
    return;
  }

  let addedTabindex = false;

  if (!container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');
    addedTabindex = true;
  }

  trapFocusHandlers.container = container;

  trapFocusHandlers.cleanup = () => {
    if (addedTabindex) {
      container.removeAttribute('tabindex');
    }
  };

  const focusFirst = () => {
    const focusable = getFocusableElements(
      container
    );

    const target = focusable[0] || container;

    target.focus({
      preventScroll: true
    });
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') {
      return;
    }

    // Always recalculate. A navigation
    // submenu might have opened or closed.
    const focusable = getFocusableElements(
      container
    );

    if (!focusable.length) {
      event.preventDefault();

      container.focus({
        preventScroll: true
      });

      return;
    }

    const first = focusable[0];
    const last = focusable[
      focusable.length - 1
    ];

    const active = document.activeElement;

    if (
      active === container ||
      !container.contains(active) ||
      !focusable.includes(active)
    ) {
      event.preventDefault();

      if (event.shiftKey) {
        last.focus();
      } else {
        first.focus();
      }

      return;
    }

    if (
      event.shiftKey &&
      active === first
    ) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      active === last
    ) {
      event.preventDefault();
      first.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (
      !(event.target instanceof Node) ||
      container.contains(event.target)
    ) {
      return;
    }

    event.stopPropagation();
    focusFirst();
  };

  document.addEventListener(
    'keydown',
    trapFocusHandlers.keydown,
    true
  );

  document.addEventListener(
    'focusin',
    trapFocusHandlers.focusin,
    true
  );

  // Establish initial keyboard focus only
  // if focus is currently outside the panel.
  if (
    !container.contains(document.activeElement)
  ) {
    focusFirst();
  }
}

/**
 * Remove the active focus trap.
 */
export function removeTrapFocus() {
  if (trapFocusHandlers.keydown) {
    document.removeEventListener(
      'keydown',
      trapFocusHandlers.keydown,
      true
    );
  }

  if (trapFocusHandlers.focusin) {
    document.removeEventListener(
      'focusin',
      trapFocusHandlers.focusin,
      true
    );
  }

  if (trapFocusHandlers.cleanup) {
    trapFocusHandlers.cleanup();
  }

  delete trapFocusHandlers.keydown;
  delete trapFocusHandlers.focusin;
  delete trapFocusHandlers.cleanup;
  delete trapFocusHandlers.container;
}

/**
 * Cycle through supplied navigation links.
 */
export function cycleFocus(items, increment) {
  const availableItems = items.filter(
    isFocusable
  );

  if (!availableItems.length) {
    return;
  }

  const currentIndex =
    availableItems.findIndex(
      (item) =>
        item === document.activeElement
    );

  let targetIndex =
    currentIndex + increment;

  if (targetIndex >= availableItems.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex =
      availableItems.length - 1;
  }

  availableItems[targetIndex]?.focus();
}
